import React, { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  formatIsoToPtBR,
  normalizeKortexResults,
  safeParseAnalysis,
} from "../../utils/processAnalysis";
import {
  compareKortexQuestionnaireItems,
  resolveKortexQuestionSortIndex,
} from "../../utils/kortexQuestionnaireOrder";
import {
  buildSourcesFromKortexResultItem,
  hasKortexDocumentLinks,
  parseAllRelevantPages,
  collectRelevantPagesItems,
} from "../../utils/kortexAnalysis";
import { decodeHtmlEntities } from "../../utils/htmlEntities";

function getKortexStatusPill(status) {
  const normalized = (status || "").toString().toUpperCase();
  switch (normalized) {
    case "COMPLETED":
    case "DONE":
      return {
        label: status || "COMPLETED",
        className: "bg-green-100 text-green-700 border border-green-200",
        Icon: CheckCircle,
      };
    case "IN_PROGRESS":
    case "RUNNING":
    case "PROCESSING":
      return {
        label: status || "IN_PROGRESS",
        className: "bg-blue-100 text-blue-700 border border-blue-200",
        Icon: Clock,
      };
    case "CREATED":
    case "PENDING":
      return {
        label: status || "PENDING",
        className: "bg-yellow-100 text-yellow-700 border border-yellow-200",
        Icon: Clock,
      };
    case "FAILED":
    case "ERROR":
      return {
        label: status || "FAILED",
        className: "bg-red-100 text-red-700 border border-red-200",
        Icon: AlertCircle,
      };
    default:
      return {
        label: status || "N/A",
        className: "bg-gray-100 text-gray-700 border border-gray-200",
        Icon: FileText,
      };
  }
}

/**
 * Trata a resposta da análise, substituindo frases específicas por versões simplificadas
 * @param {string} answer - Resposta original
 * @returns {string} - Resposta tratada
 */
function treatAnswer(answer) {
  if (!answer) return "N/A";

  const text = decodeHtmlEntities(String(answer));

  const phraseToReplace =
    "não foi possível encontrar contexto relevante nos documentos da análise";

  if (text.toLowerCase().includes(phraseToReplace.toLowerCase())) {
    return "Não.";
  }

  return text;
}

function treatReasoning(reasoning) {
  if (!reasoning) return null;
  const text = decodeHtmlEntities(String(reasoning)).trim();
  return text ? text : null;
}

function StatusPill({ status }) {
  const { label, className, Icon } = getKortexStatusPill(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${className}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

function normalizeQuestionKey(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeCsvKeyForGroup(item) {
  const header = (item?.header || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .trim();

  if (header) return `csv:${header}`;

  return `q:${normalizeQuestionKey(item?.question_text) || "pergunta_nao_informada"}`;
}

function normalizeTextValue(value) {
  if (value == null) return "";
  return decodeHtmlEntities(String(value)).trim();
}

function appendUniqueText(existingValue, nextValue, separator = " / ") {
  const existing = normalizeTextValue(existingValue);
  const next = normalizeTextValue(nextValue);
  if (!next) return existingValue ?? null;
  if (!existing) return next;

  const currentParts = existing
    .split(separator)
    .map((part) => part.trim())
    .filter(Boolean);

  if (currentParts.includes(next)) return existing;
  return `${existing}${separator}${next}`;
}

function addUnique(list, value) {
  const text = normalizeTextValue(value);
  if (!text) return list;
  return list.includes(text) ? list : [...list, text];
}

function normalizeIsoDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function pickLatestIsoDate(a, b) {
  const aIso = normalizeIsoDate(a);
  const bIso = normalizeIsoDate(b);
  if (!aIso) return bIso;
  if (!bIso) return aIso;
  return new Date(bIso).getTime() > new Date(aIso).getTime() ? bIso : aIso;
}

function answerIndicatesNoKortexDocument(answer) {
  const text = normalizeTextValue(answer).toLowerCase();
  return text.includes("nenhum documento associado");
}

function hasKortexLinkedDocument(source) {
  if (answerIndicatesNoKortexDocument(source?.answer)) return false;
  return hasKortexDocumentLinks(source);
}

function hasKortexLinkedMovement(source, { rawDocumentDate, rawMovementDate }) {
  if (!rawMovementDate) return false;

  const movementId =
    source?.movement_id ??
    source?.movementId ??
    source?.movementID ??
    source?.event_id ??
    source?.eventId ??
    null;

  if (movementId != null && String(movementId).trim() !== "") return true;

  if (!hasKortexLinkedDocument(source)) {
    return !answerIndicatesNoKortexDocument(source?.answer);
  }

  if (rawDocumentDate && rawMovementDate !== rawDocumentDate) return true;

  return false;
}

/**
 * Datas exibidas somente quando há documento ou movimentação vinculados na resposta.
 */
function resolveKortexSourceDisplayDates(source) {
  const rawDocumentDate = normalizeIsoDate(
    source?.documentDate ??
      source?.document_date ??
      source?.document_date_time ??
      source?.document_datetime,
  );
  const rawMovementDate = normalizeIsoDate(
    source?.movementDate ??
      source?.movement_date ??
      source?.movement_date_time ??
      source?.movement_datetime,
  );

  const linkedDocument = hasKortexLinkedDocument(source);
  const linkedMovement = hasKortexLinkedMovement(source, {
    rawDocumentDate,
    rawMovementDate,
  });

  return {
    documentDate: linkedDocument && rawDocumentDate ? rawDocumentDate : null,
    movementDate: linkedMovement && rawMovementDate ? rawMovementDate : null,
  };
}

function applyKortexSourceDisplayDates(source) {
  const displayDates = resolveKortexSourceDisplayDates(source);
  return {
    ...source,
    documentDate: displayDates.documentDate,
    movementDate: displayDates.movementDate,
  };
}

function mergeSourceMetadata(target, source) {
  target.document_type = appendUniqueText(target.document_type, source.document_type);
  target.cnj = appendUniqueText(target.cnj, source.cnj);
  target.documentDate = pickLatestIsoDate(target.documentDate, source.documentDate);
  target.movementDate = pickLatestIsoDate(target.movementDate, source.movementDate);
  target.header = appendUniqueText(target.header, source.header, "\n\n");
  target.reasoning = appendUniqueText(target.reasoning, source.reasoning, "\n\n");
  if (source.document_url && !target.document_url) {
    target.document_url = source.document_url;
  }
  const incomingPages = source.relevant_pages_items?.length
    ? source.relevant_pages_items
    : collectRelevantPagesItems(source.relevant_pages);
  incomingPages.forEach((pageBlock) => {
    target.relevant_pages_items = addUnique(
      target.relevant_pages_items || [],
      pageBlock,
    );
  });
  target.relevant_pages = target.relevant_pages_items[0] ?? target.relevant_pages ?? null;

  const targetTime = target.created_at ? new Date(target.created_at).getTime() : 0;
  const sourceTime = source.created_at ? new Date(source.created_at).getTime() : 0;
  if (sourceTime > targetTime) {
    target.created_at = source.created_at;
  }

  target.duplicateCount = (target.duplicateCount || 1) + 1;
  return target;
}

export default function ProcessAnalysisPanel({
  analysis,
  isExpanded,
  onToggle,
}) {
  const analysisObj = useMemo(() => safeParseAnalysis(analysis), [analysis]);

  const analyses = useMemo(() => {
    const list = analysisObj?.analyses;
    return Array.isArray(list) ? list : [];
  }, [analysisObj]);

  const results = useMemo(() => {
    const list = normalizeKortexResults(analysisObj?.results);
    // O resumo será exibido em um card separado na tela, então removemos daqui
    return list.filter((r) => {
      const q = (r?.question_text || "").toString().trim().toLowerCase();
      return !(q === "resumo" || q.startsWith("resumo"));
    });
  }, [analysisObj]);

  const orderedResults = useMemo(() => {
    const parseFiniteNumber = (value) => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      if (typeof value === "string" && value.trim() !== "") {
        const n = Number(value);
        if (Number.isFinite(n)) return n;
      }
      return null;
    };

    const extractOrderKey = (item) => {
      // Preferir campos explícitos de ordenação, se existirem no payload.
      const explicit =
        parseFiniteNumber(item?.question_order) ??
        parseFiniteNumber(item?.questionOrder) ??
        parseFiniteNumber(item?.order) ??
        parseFiniteNumber(item?.position) ??
        parseFiniteNumber(item?.index) ??
        parseFiniteNumber(item?.question_index) ??
        parseFiniteNumber(item?.questionIndex);
      if (explicit != null) return explicit;

      // `question_id` normalmente identifica a pergunta do template; o `id`
      // do resultado pode refletir apenas a ordem de criação das respostas.
      const questionIdKey = parseFiniteNumber(item?.question_id ?? item?.questionId);
      if (questionIdKey != null) return questionIdKey;

      return null;
    };

    const enriched = results.map((r, originalIndex) => ({
      ...r,
      __originalIndex: originalIndex,
      __templateOrderIndex: resolveKortexQuestionSortIndex(r, originalIndex),
      __orderKey: extractOrderKey(r),
      __questionKey: normalizeQuestionKey(r?.question_text),
      __groupKey: normalizeCsvKeyForGroup(r),
    }));

    enriched.sort(compareKortexQuestionnaireItems);

    return enriched;
  }, [results]);

  const consolidatedResults = useMemo(() => {
    const groups = new Map();

    for (const item of orderedResults) {
      const rawQuestionText = normalizeTextValue(item?.question_text);
      const hasQuestionText = rawQuestionText !== "";
      const questionText = hasQuestionText ? rawQuestionText : "Pergunta não informada";

      const groupKey =
        item?.__groupKey ||
        normalizeCsvKeyForGroup(item) ||
        `pergunta:${item?.__originalIndex ?? "unknown"}`;

      const itemSources = buildSourcesFromKortexResultItem(item).map(
        applyKortexSourceDisplayDates,
      );

      for (const source of itemSources) {
        const existing = groups.get(groupKey);
        if (!existing) {
          groups.set(groupKey, {
            question_text: questionText,
            header: item?.header ?? source.header ?? null,
            // Compatibilidade: manter uma "resposta primária" no root
            // para que UI/fallbacks antigos não quebrem.
            answer: source.answer ?? null,
            __templateOrderIndex:
              item?.__templateOrderIndex ??
              resolveKortexQuestionSortIndex(item, item?.__originalIndex ?? 0),
            __orderKey: item?.__orderKey ?? null,
            __originalIndex: item?.__originalIndex ?? 0,
            created_at: item?.created_at ?? source.created_at ?? null,
            sources: [source],
          });
          continue;
        }

        const itemTemplateIndex =
          item?.__templateOrderIndex ??
          resolveKortexQuestionSortIndex(item, item?.__originalIndex ?? 0);
        existing.__templateOrderIndex = Math.min(
          existing.__templateOrderIndex ?? itemTemplateIndex,
          itemTemplateIndex,
        );

        // Mantém a melhor chave de ordenação possível (menor) para respeitar o formulário.
        if (existing.__orderKey == null && item?.__orderKey != null) {
          existing.__orderKey = item.__orderKey;
        } else if (existing.__orderKey != null && item?.__orderKey != null) {
          existing.__orderKey = Math.min(existing.__orderKey, item.__orderKey);
        }

        existing.__originalIndex = Math.min(
          existing.__originalIndex,
          item?.__originalIndex ?? 0,
        );

        // Para exibição no summary, usa a data mais recente entre as fontes.
        const existingTime = existing.created_at
          ? new Date(existing.created_at).getTime()
          : 0;
        const itemTime = item?.created_at ? new Date(item.created_at).getTime() : 0;
        if (itemTime > existingTime) {
          existing.created_at = item.created_at;
        }

        // Evita duplicatas exatas de resposta dentro do mesmo grupo.
        const incomingAnswer = normalizeTextValue(source.answer);
        const sameAnswerSource = existing.sources.find(
          (s) =>
            normalizeTextValue(s.answer) === incomingAnswer && incomingAnswer !== "",
        );

        if (sameAnswerSource) {
          mergeSourceMetadata(sameAnswerSource, source);
        } else {
          existing.sources.push(source);
        }

        // Compatibilidade: preencher resposta primária quando não existir.
        if (normalizeTextValue(existing.answer) === "" && incomingAnswer !== "") {
          existing.answer = source.answer ?? null;
        }
      }
    }

    const consolidated = Array.from(groups.values()).map((group) => {
      if (Array.isArray(group.sources) && group.sources.length > 0) {
        return group;
      }

      return {
        ...group,
        sources: buildSourcesFromKortexResultItem(group).map(
          applyKortexSourceDisplayDates,
        ),
      };
    });

    consolidated.sort(compareKortexQuestionnaireItems);

    return consolidated;
  }, [orderedResults]);

  const sortedAnalyses = useMemo(() => {
    return [...analyses].sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [analyses]);

  const [resultQuery, setResultQuery] = useState("");
  const [visibleResultsCount, setVisibleResultsCount] = useState(20);

  const filteredResults = useMemo(() => {
    const normalizeForSearch = (value) =>
      decodeHtmlEntities(String(value ?? ""))
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

    const q = normalizeForSearch(resultQuery);
    if (!q) return consolidatedResults;
    return consolidatedResults.filter((r) => {
      const sources = Array.isArray(r?.sources) ? r.sources : [];
      const sourcesText = sources
        .map(
          (s) =>
            `${s?.answer || ""} ${s?.document_type || ""} ${s?.header || ""} ${
              s?.cnj || ""
            } ${s?.relevant_pages || ""} ${s?.reasoning || ""} ${s?.documentDate || ""} ${s?.movementDate || ""}`,
        )
        .join(" ");

      const haystack = normalizeForSearch(`${r?.question_text || ""} ${sourcesText}`);
      return haystack.includes(q);
    });
  }, [consolidatedResults, resultQuery]);

  const visibleResults = useMemo(() => {
    return filteredResults.slice(0, visibleResultsCount);
  }, [filteredResults, visibleResultsCount]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Header clicável */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-gray-900">
                Análise (Kortex)
              </h3>
              {analysisObj?.status && <StatusPill status={analysisObj.status} />}
            </div>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {analysisObj
                  ? "Questionário, resultados e documentos — clique para expandir"
                  : "Análise pendente — clique para expandir"}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
        )}
      </button>

      {/* Conteúdo expandível */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <div className="mt-4">
            <p className="text-sm text-gray-700 mb-4">
                  {analysisObj?.analysis_folder_name ? (
                <>
                  <span className="font-medium text-gray-900 bg-blue-50 px-2 py-0.5 rounded">
                    {decodeHtmlEntities(String(analysisObj.analysis_folder_name))}
                  </span>
                  {analysisObj?.analysis_folder_id !== undefined &&
                    analysisObj?.analysis_folder_id !== null && (
                      <span className="text-gray-600 ml-2">
                        - Folder #{analysisObj.analysis_folder_id}
                      </span>
                    )}
                </>
              ) : (
                "Sem análise disponível para este processo."
              )}
            </p>

            {!analysisObj ? (
              <div className="bg-white border border-yellow-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900 mb-1">
                      Nenhuma análise
                    </div>
                    <div className="text-gray-700">
                      Quando a análise do Kortex estiver disponível, ela
                      aparecerá aqui automaticamente.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                      Criada em
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {formatIsoToPtBR(analysisObj?.created_at)}
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">
                      Concluída em
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {analysisObj?.completed_at ? (
                        formatIsoToPtBR(analysisObj?.completed_at)
                      ) : (
                        <span className="text-orange-600">Em andamento</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">
                      Análises
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {analyses.length}
                    </div>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                      Resultados
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {results.length}
                    </div>
                  </div>
                </div>

                {/* Analyses */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-purple-500 rounded"></div>
                    Análises Realizadas
                  </h4>
                  {sortedAnalyses.length === 0 ? (
                    <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      Nenhuma análise (item) encontrada no payload.
                    </div>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-purple-600">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                              Título
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                              Criada em
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortedAnalyses.map((a, idx) => (
                            <tr
                              key={a?.id ?? `${a?.title}-${a?.created_at}`}
                              className={`hover:bg-purple-50 transition-colors ${
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {a?.title != null
                                    ? decodeHtmlEntities(String(a.title))
                                    : "N/A"}
                                </div>
                                {a?.description ? (
                                  <div className="text-xs text-gray-600 mt-1">
                                    {decodeHtmlEntities(String(a.description))}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-4 py-3">
                                <StatusPill status={a?.status} />
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {formatIsoToPtBR(a?.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Results */}
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <div className="w-1 h-4 bg-indigo-500 rounded"></div>
                      Resultados (Q&A)
                      <span className="ml-2 text-xs font-medium text-gray-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                        {filteredResults.length}
                      </span>
                    </h4>
                  </div>

                  {filteredResults.length === 0 ? (
                    <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      Nenhum resultado encontrado.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visibleResults.map((r, idx) => (
                        <details
                          key={r?.id ?? `${r?.question_text}-${r?.created_at}`}
                          className="group border border-indigo-200 rounded-lg bg-white hover:border-indigo-300 transition-all"
                        >
                          <summary className="cursor-pointer select-none px-4 py-3 flex items-start justify-between gap-4 hover:bg-indigo-50 rounded-lg transition-colors">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="mt-1 w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0"></div>
                                <div className="text-sm font-medium text-gray-900">
                                  {r?.question_text != null && r.question_text !== ""
                                    ? decodeHtmlEntities(String(r.question_text))
                                    : "Pergunta não informada"}
                                </div>
                                {Array.isArray(r?.sources) && r.sources.length > 1 ? (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                    {r.sources.length} respostas
                                  </span>
                                ) : null}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600 ml-3.5">
                                {Array.isArray(r?.sources) &&
                                  (() => {
                                    const documentTypes = Array.from(
                                      new Set(
                                        r.sources
                                          .map((s) => s?.document_type)
                                          .filter(Boolean)
                                          .map((v) => String(v)),
                                      ),
                                    ).slice(0, 4);

                                    const cnjs = Array.from(
                                      new Set(
                                        r.sources
                                          .map((s) => s?.cnj)
                                          .filter(Boolean)
                                          .map((v) => String(v)),
                                      ),
                                    ).slice(0, 4);

                                    return (
                                      <>
                                        {documentTypes.length > 0 && (
                                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                                            Doc. {documentTypes.map(decodeHtmlEntities).join(" / ")}
                                          </span>
                                        )}
                                        {cnjs.length > 0 && (
                                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                            CNJ {cnjs.map(decodeHtmlEntities).join(" / ")}
                                          </span>
                                        )}
                                      </>
                                    );
                                  })()}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-200 flex-shrink-0">
                              {formatIsoToPtBR(r?.created_at)}
                            </div>
                          </summary>

                          <div className="px-4 pb-4 pt-2 border-t border-indigo-100 bg-gray-50 rounded-b-lg">
                            {Array.isArray(r?.sources) && r.sources.length > 0 ? (
                              <div className="pt-3 space-y-3">
                                {r.sources.map((s, sIdx) => (
                                  <div
                                    key={s?.id ?? `${s?.created_at}-${sIdx}`}
                                    className="bg-white border border-gray-200 rounded-lg p-3"
                                  >
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
                                        {s?.document_type ? (
                                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                                            Doc. {decodeHtmlEntities(String(s.document_type))}
                                          </span>
                                        ) : null}
                                        {s?.cnj ? (
                                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                            CNJ {decodeHtmlEntities(String(s.cnj))}
                                          </span>
                                        ) : null}
                                        {s?.documentDate ? (
                                          <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium border border-gray-200">
                                            Doc.: {formatIsoToPtBR(s.documentDate, { withTime: false })}
                                          </span>
                                        ) : null}
                                        {s?.movementDate ? (
                                          <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium border border-gray-200">
                                            Mov.: {formatIsoToPtBR(s.movementDate, { withTime: false })}
                                          </span>
                                        ) : null}
                                        {s?.duplicateCount > 1 ? (
                                          <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium">
                                            {s.duplicateCount} ocorrências
                                          </span>
                                        ) : null}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {formatIsoToPtBR(s?.created_at)}
                                      </div>
                                    </div>

                                    {s?.header ? (
                                      <div className="text-xs text-gray-700 mb-2">
                                        <span className="font-semibold text-gray-700">
                                          Cabeçalho:
                                        </span>{" "}
                                        <span className="text-gray-700">
                                          {decodeHtmlEntities(String(s.header))}
                                        </span>
                                      </div>
                                    ) : null}

                                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                      <pre className="whitespace-pre-wrap text-sm text-gray-800">
                                        {treatAnswer(s?.answer)}
                                      </pre>
                                    </div>

                                    {treatReasoning(s?.reasoning) ? (
                                      <details className="mt-3 group/reasoning">
                                        <summary className="cursor-pointer select-none flex items-center justify-between gap-3 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors">
                                          <div className="flex items-center gap-2">
                                            <div className="w-1 h-3 bg-gray-500 rounded"></div>
                                            Explicação da IA
                                          </div>
                                          <span className="text-gray-500 group-open/reasoning:hidden">
                                            Mostrar
                                          </span>
                                          <span className="text-gray-500 hidden group-open/reasoning:inline">
                                            Ocultar
                                          </span>
                                        </summary>
                                        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3">
                                          <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                                            {treatReasoning(s?.reasoning)}
                                          </pre>
                                        </div>
                                      </details>
                                    ) : null}

                                    {(s?.document_url ||
                                      s?.relevant_pages_items?.length > 0 ||
                                      s?.relevant_pages) &&
                                      (() => {
                                        const rawRelevantPages =
                                          s?.relevant_pages_items?.length > 0
                                            ? s.relevant_pages_items
                                            : collectRelevantPagesItems(s.relevant_pages);
                                        const parsedPages = rawRelevantPages.flatMap(
                                          (raw) => parseAllRelevantPages(raw),
                                        );

                                        const directLinks = s?.document_url
                                          ? [
                                              {
                                                label: s?.document_type
                                                  ? decodeHtmlEntities(String(s.document_type))
                                                  : "Documento",
                                                page: null,
                                                url: String(s.document_url),
                                              },
                                            ]
                                          : [];

                                        const allLinks = [...directLinks, ...parsedPages];

                                        if (allLinks.length > 0) {
                                          return (
                                            <div className="mt-3">
                                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                                Páginas relevantes:
                                              </div>
                                              <div className="flex flex-wrap gap-2">
                                                {allLinks.map((pageInfo, idx) => (
                                                  <a
                                                    key={`${pageInfo.url}-${idx}`}
                                                    href={pageInfo.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 border border-blue-200 hover:border-blue-300 rounded-md transition-colors text-xs font-medium"
                                                    title={
                                                      pageInfo.page
                                                        ? `Abrir ${pageInfo.label} - Página ${pageInfo.page}`
                                                        : `Abrir ${pageInfo.label}`
                                                    }
                                                  >
                                                    <FileText className="w-3 h-3" />
                                                    <span>
                                                      {decodeHtmlEntities(
                                                        String(pageInfo.label),
                                                      )}
                                                    </span>
                                                    {pageInfo.page ? (
                                                      <span className="text-blue-600">
                                                        - Pág. {pageInfo.page}
                                                      </span>
                                                    ) : null}
                                                  </a>
                                                ))}
                                              </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div className="mt-3 text-xs text-gray-700">
                                            <span className="font-semibold text-gray-700">
                                              Páginas relevantes:
                                            </span>{" "}
                                            <span className="text-gray-700">
                                              {decodeHtmlEntities(
                                                String(
                                                  rawRelevantPages.filter(Boolean).join("\n\n"),
                                                ),
                                              )}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="pt-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-1 h-3 bg-purple-500 rounded"></div>
                                  <div className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                                    Resposta
                                  </div>
                                </div>
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                  <pre className="whitespace-pre-wrap text-sm text-gray-800">
                                    {treatAnswer(r?.answer)}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </details>
                      ))}

                      {visibleResults.length < filteredResults.length && (
                        <button
                          onClick={() =>
                            setVisibleResultsCount((prev) => prev + 20)
                          }
                          className="w-full mt-3 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Mostrar mais (
                          {filteredResults.length - visibleResults.length}{" "}
                          restantes)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
